import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Grid,
    CircularProgress,
    Avatar
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    QrCode as QrCodeIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import * as QRCampaignsService from '../../services/qrcampaigns.service';
import {formatCurrency} from '../../utils/Functions';

const QRCampaigns = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {requesting} = useSelector((state) => state.Process);

    const [campaigns, setCampaigns] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadCampaigns();
        loadStats();
    }, []);

    const loadCampaigns = async () => {
        setLoading(true);
        try {
            const response = await QRCampaignsService.getAllQRCampaigns(dispatch);
            if (response && response.status) {
                setCampaigns(response.result || []);
            }
        } catch (error) {
            console.error('Error loading campaigns:', error);
        }
        setLoading(false);
    };

    const loadStats = async () => {
        try {
            const response = await QRCampaignsService.getQRCampaignStats(dispatch);
            if (response && response.status) {
                setStats(response.result[0] || {});
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const handleCreate = () => {
        navigate(process.env.REACT_APP_ADMMIN_PATH + '/manage/qrcampaigns/create/new');
    };

    const handleEdit = (id) => {
        navigate(process.env.REACT_APP_ADMMIN_PATH + `/manage/qrcampaigns/edit/${id}`);
    };

    const handleView = (id) => {
        navigate(process.env.REACT_APP_ADMMIN_PATH + `/manage/qrcampaigns/view/${id}`);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar esta campaña?')) {
            try {
                const response = await QRCampaignsService.deleteQRCampaign(dispatch, {id});
                if (response && response.status) {
                    loadCampaigns();
                    loadStats();
                }
            } catch (error) {
                console.error('Error deleting campaign:', error);
            }
        }
    };

    const handleDownloadQR = (qrImage, campaignName) => {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${qrImage}`;
        link.download = `QR_${campaignName}.png`;
        link.click();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'success';
            case 'INACTIVE': return 'info';
            case 'EXPIRED': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Activa';
            case 'INACTIVE': return 'Inactiva';
            case 'EXPIRED': return 'Expirada';
            default: return status;
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Campañas QR
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    sx={{ backgroundColor: '#009ce0' }}
                >
                    Nueva Campaña
                </Button>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="textSecondary">
                                Total Campañas
                            </Typography>
                            <Typography variant="h3" color="primary">
                                {stats.total_campaigns || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="textSecondary">
                                Campañas Activas
                            </Typography>
                            <Typography variant="h3" color="success.main">
                                {stats.active_campaigns || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="textSecondary">
                                Total Recaudado
                            </Typography>
                            <Typography variant="h3" color="primary">
                                {formatCurrency(stats.total_collected || 0, 'BOB')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" color="textSecondary">
                                Total Transacciones
                            </Typography>
                            <Typography variant="h3" color="info.main">
                                {stats.total_transactions || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Campaigns Table */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Lista de Campañas
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleCreate}
                            size="small"
                        >
                            Nueva Campaña
                        </Button>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>QR</TableCell>
                                    <TableCell>Campaña</TableCell>
                                    <TableCell>Monto</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell>Usos</TableCell>
                                    <TableCell>Recaudado</TableCell>
                                    <TableCell>Fecha Expiración</TableCell>
                                    <TableCell align="center">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {campaigns.map((campaign) => (
                                    <TableRow key={campaign.CAMPAIGNID}>
                                        <TableCell>
                                            <Avatar
                                                src={`data:image/png;base64,${campaign.QR_IMAGE}`}
                                                variant="square"
                                                sx={{ width: 40, height: 40 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2">
                                                {campaign.CAMPAIGNNAME}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {campaign.DESCRIPTION}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="h6">
                                                {formatCurrency(campaign.AMOUNT, 'BOB')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(campaign.STATUS)}
                                                color={getStatusColor(campaign.STATUS)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {campaign.CURRENT_USES} / {campaign.MAX_USES === 0 ? '∞' : campaign.MAX_USES}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="h6" color="primary">
                                                {formatCurrency(campaign.TOTAL_COLLECTED || 0, 'BOB')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {new Date(campaign.EXPIRATION_DATE).toLocaleDateString('es-ES')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleView(campaign.CAMPAIGNID)}
                                                title="Ver detalles"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownloadQR(campaign.QR_IMAGE, campaign.CAMPAIGNNAME)}
                                                title="Descargar QR"
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(campaign.CAMPAIGNID)}
                                                title="Eliminar"
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {campaigns.length === 0 && (
                        <Box textAlign="center" py={4}>
                            <QrCodeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                No hay campañas creadas
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Crea tu primera campaña QR para comenzar a recaudar donaciones
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCreate}
                                sx={{ backgroundColor: '#009ce0' }}
                            >
                                Crear Primera Campaña
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default QRCampaigns;